
import React, { useState, useEffect } from 'react';
import StandardModal, { StandardModalBody, StandardModalFooter, useModalLoading } from '../../../components/modales/StandardModal';
import axios from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { StatusChip, type DataTableColumn } from '@routes-plan/ui';
import { formatCurrency } from '../../../utils/format/currency';
import { ModalTable } from '../components/ReportLayout';
import moment from 'moment-timezone';
import { useT } from '../../../i18n';

interface HistoricoModalProps {
  open: boolean;
  onClose: () => void;
  clientId: string | null; // Permitir null
  routeId: string | null; // Permitir null
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface HistoryRow {
  id: number;
  active: boolean;
  state: string;
  date: string;
  statusDate: string;
  value: number;
  route: string;
}

/** Dato de la ficha del cliente: etiqueta pequeña y valor. */
const InfoField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500} sx={{ overflowWrap: 'anywhere' }}>
      {value === '' || value === null || value === undefined ? '—' : value}
    </Typography>
  </Box>
);

interface ClientInfo {
  id: number;
  idNumber: string;
  fullName: string;
  phone: string;
  mobilePhone: string;
  mainAddress: string;
  secondaryAddress: string;
  job: string;
  coSignerFullName: string;
  coSignerIdNumber: string;
  coSignerAddress: string;
  coSignerPhone: string;
  route: {
    id: number;
    name: string;
    startDate: string;
    openingTime: string;
    closingTime: string;
    creditLimit: number;
    deviceDetail: boolean;
    enforceSchedule: boolean;
    allowRepay: boolean;
    allowMultipleLoans: boolean;
    allowRefinance: boolean;
  };
  loans: {
    id: number;
    date: string;
    statusDate: string;
    value: number;
    totalValue: number;
    balance: number;
    interestRate: number;
    installmentValue: number;
    termDays: number;
    daysLate: number;
    state: number;
    projectedPayment: number;
    paidValue: number;
    currentDay: number;
    visitOrderId: number;
    visitDay: string | null;
    position: number;
    contract: string;
    paymentCount: number;
    location: string;
  }[];
}
const PaymentHistoryModal: React.FC<HistoricoModalProps> = ({
  open,
  onClose,
  routeId,
  clientId,
  showAlert,
  showLoading,
  hideLoading,
}) => {
  const t = useT();
  // El Cargando general queda detrás del modal: se usa el del modal estándar.
  const { loading: modalBusy, showLoading: showModalLoading, hideLoading: hideModalLoading } = useModalLoading();
  const [clientInfo, setClientInfo] = useState<ClientInfo | null>(null);
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [inputs, setInputs] = useState({
    cedula: '',
    nombre: '',
    direccion: '',
    telefono: '',
    oficio: '',
    calificacion: '',
  });

  useEffect(() => {
    const fetchClientInfo = async () => {
      try {
        showModalLoading();
        if (!clientId) {
          return;
        }
        const url = "/clients/lookup";
        const formData: any = {
          idNumber: clientId,
        };
        if (routeId) {
          formData.routeId = routeId;
        }
        const response = await axios.post(url, formData);
        if (response.data.data.length > 0) {
          const info = response.data.data[0] as ClientInfo;
          setClientInfo(info);
          setInputs({
            cedula: info.idNumber?.toString() || '',
            nombre: info.fullName || '',
            direccion: info.mainAddress || '',
            telefono: info.phone || '',
            oficio: info.job || '',
            calificacion: '',
          });
          const mappedRows = (info.loans || []).map((loan) => ({
            id: loan.id,
            active: loan.state === 0,
            state: loan.state === 0 ? t('paymentReports.history.stateActive') : t('paymentReports.history.statePaidOff'),
            date: loan.date ? moment(loan.date).format('YYYY/MM/DD') : '',
            statusDate: loan.statusDate ? moment(loan.statusDate).format('YYYY/MM/DD') : '',
            value: loan.value ?? 0,
            route: info.route?.name || '',
          }));
          setRows(mappedRows);
        } else {
          setClientInfo(null);
          setRows([]);
          showAlert(t('paymentReports.history.noClientData'), 'info');
        }
      } catch (error) {
        showAlert(t('paymentReports.history.loadError'), 'error');
      } finally {
        hideModalLoading();
      }
    };

    if (open) {
      fetchClientInfo();
    }
  }, [open, routeId, clientId, showAlert, showModalLoading, hideModalLoading, t]);

  const handleClose = () => {
    setClientInfo(null);
    setRows([]);
    onClose();
  };

  const historialColumns: DataTableColumn<HistoryRow>[] = [
    { id: 'id', header: t('paymentReports.history.colNumber'), value: (r) => r.id, sortable: true },
    { id: 'state', header: t('common.status'), value: (r) => r.state, render: (r) => <StatusChip label={r.state} tone={r.active ? 'info' : 'neutral'} />, sortable: true },
    { id: 'date', header: t('common.date'), value: (r) => r.date, sortable: true },
    { id: 'statusDate', header: t('paymentReports.history.colPaidOffDate'), value: (r) => r.statusDate, hideBelow: 'sm' },
    { id: 'value', header: t('paymentReports.history.colValue'), value: (r) => Number(r.value ?? 0), render: (r) => formatCurrency(Number(r.value ?? 0)), align: 'right', sortable: true },
    { id: 'route', header: t('summaryReports.shared.route'), value: (r) => r.route, hideBelow: 'sm' },
  ];

  return (
    <StandardModal
      open={open}
      onClose={handleClose}
      title={t('paymentReports.history.title')}
      subtitle={inputs.nombre || undefined}
      maxWidth={860}
      loading={modalBusy}
      composed
    >
      <StandardModalBody>
        <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(150px, 100%), 1fr))', gap: 2 }}>
            <InfoField label={t('common.idNumber')} value={inputs.cedula} />
            <InfoField label={t('common.name')} value={inputs.nombre} />
            <InfoField label={t('common.address')} value={inputs.direccion} />
            <InfoField label={t('common.phone')} value={inputs.telefono} />
            <InfoField label={t('paymentReports.history.job')} value={inputs.oficio} />
            <InfoField label={t('paymentReports.history.rating')} value={inputs.calificacion} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" component="h3" sx={{ mb: 1, fontWeight: 600 }}>
              {t('paymentReports.history.loanHistory')}
            </Typography>
            <ModalTable
              rows={rows}
              columns={historialColumns}
              getRowId={(r) => r.id}
              searchable={false}
              initialPageSize={10}
              pageSizeOptions={[10, 25, 50]}
              maxHeight="min(40vh, 360px)"
              ariaLabel={t('paymentReports.history.loanHistory')}
            />
          </Box>
        </Box>
      </StandardModalBody>
      <StandardModalFooter>
        <span />
        <Button variant="outlined" onClick={handleClose}>
          {t('common.close')}
        </Button>
      </StandardModalFooter>
    </StandardModal>
  );
};

export default PaymentHistoryModal;
